import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from './Header';
import SearchBar from './SearchBar';
import StudentTable from './StudentTable';
import NewStudentModal from './NewStudentModal';
import ViewStudentModal from './ViewStudentModal';
import styles from '../../styles/AdmisionEstudiantesStyles/AdmisionEstudiantes.module.css';
import { getStudents, getStudent, createStudent, updateStudent, deleteStudent } from '../../api/students';

function AdmisionEstudiantesScreen() {
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const data = await getStudents({ no_pagination: true });
        setStudents(data || []);
      } catch (error) {
        console.error('Error loading students:', error);
        setErrorMessage('No se pudieron cargar los estudiantes.');
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, [refreshTrigger]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleNewStudent = () => {
    setEditMode(false);
    setSelectedStudent(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedStudent(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedStudent(null);
  };

  const convertDateFormat = (dateStr) => {
    if (!dateStr) return null;
    // Si es formato DD/MM/YYYY convertir a YYYY-MM-DD
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    return dateStr;
  };

  const handleSaveStudent = async (studentData) => {
    try {
      setLoading(true);
      setErrorMessage('');
      // Transformar datos del formulario al formato del backend
      const payload = {
        nombre_completo: studentData.nombreCompleto || '',
        id_institucion: studentData.institucion ? Number(studentData.institucion) : undefined,
        id_campus: studentData.campus ? Number(studentData.campus) : undefined,
        id_programa_actual: studentData.programa ? Number(studentData.programa) : undefined,
        ciclo_ingreso: studentData.cicloEscolar ? Number(studentData.cicloEscolar) : undefined,
        matricula: studentData.matricula || '',
        curp: studentData.curp || null,
        fecha_nacimiento: convertDateFormat(studentData.fechaNacimiento) || null,
        lugar_nacimiento: studentData.lugarNacimiento || '',
        sexo: studentData.sexo || 'M',
        estado_civil: studentData.estadoCivil || null,
        nacionalidad: studentData.nacionalidad || null,
        pais_origen: studentData.paisOrigen || null,
        email_personal: studentData.emailPersonal || null,
        email_institucional: studentData.emailInstitucional || null,
        telefono_principal: studentData.telefonoPrincipal || null,
        telefono_emergencia: studentData.telefonoEmergencia || null,
        contacto_emergencia: studentData.contactoEmergencia || null,
        estatus_estudiante: studentData.estatusEstudiante || 'ACTIVO',
        
        // Domicilio
        calle: studentData.calleNumero || null,
        colonia: studentData.colonia || null,
        ciudad: studentData.ciudad || null,
        estado: studentData.estado || null,
        codigo_postal: studentData.codigoPostal || null,
        pais: studentData.pais || null,
        
        // Información académica
        periodo_inicial: studentData.periodoInicial ? Number(studentData.periodoInicial) : null,
        tipo_ingreso: studentData.tipoIngreso || null,
        institucion_procedencia: studentData.institucionProcedencia || null,
        promedio_procedencia: studentData.promedioProce ? parseFloat(studentData.promedioProce) : null,
      };

      // Limpiar valores undefined
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log('Enviando estudiante al backend:', payload);
      
      if (editMode && selectedStudent) {
        await updateStudent(selectedStudent.id_estudiante, payload);
        alert('Estudiante actualizado correctamente');
      } else {
        await createStudent(payload);
        alert('Estudiante guardado correctamente');
      }
      
      setRefreshTrigger(prev => prev + 1);
      setShowModal(false);
      setEditMode(false);
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error al guardar estudiante:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data 
        ? JSON.stringify(error.response.data) 
        : error.message || 'Error desconocido';
      setErrorMessage('Error al guardar el estudiante.');
      alert('Error al guardar el estudiante: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este estudiante?')) {
      return;
    }
    try {
      setLoading(true);
      setErrorMessage('');
      await deleteStudent(studentId);
      setRefreshTrigger(prev => prev + 1);
      alert('Estudiante dado de baja correctamente');
    } catch (error) {
      console.error('Error al eliminar estudiante:', error);
      setErrorMessage('Error al eliminar el estudiante.');
      alert('Error al eliminar el estudiante');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStudent = async (studentId) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const student = await getStudent(studentId);

      const getIdValue = (value, objectKey) => {
        if (value && typeof value === 'object') {
          return value[objectKey] ?? '';
        }
        return value ?? '';
      };

      setSelectedStudent({
        ...student,
        id_institucion: getIdValue(student.id_institucion, 'id_institucion'),
        id_campus: getIdValue(student.id_campus, 'id_campus'),
        id_programa_actual: getIdValue(student.id_programa_actual, 'id_programa'),
        ciclo_ingreso: getIdValue(student.ciclo_ingreso, 'id_ciclo_escolar'),
      });
      setEditMode(true);
      setShowModal(true);
    } catch (error) {
      console.error('Error al cargar estudiante:', error);
      setErrorMessage('Error al cargar los datos del estudiante.');
      alert('Error al cargar los datos del estudiante');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = async (studentId) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const student = await getStudent(studentId);
      setSelectedStudent(student);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error al cargar estudiante:', error);
      setErrorMessage('Error al cargar los datos del estudiante.');
      alert('Error al cargar los datos del estudiante');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar estudiantes
  const filteredStudents = students.filter(student => {
    if (searchTerm === '') return true;
    const searchLower = searchTerm.toLowerCase();
    return (student.nombre_completo && student.nombre_completo.toLowerCase().includes(searchLower)) ||
           (student.matricula && student.matricula.toLowerCase().includes(searchLower)) ||
           (student.programa_nombre && student.programa_nombre.toLowerCase().includes(searchLower));
  });

  return (
    <div className={styles.layout}>
      <div className={styles.sidebarColumn}>
        <Sidebar />
      </div>
      
      <div className={styles.contentColumn}>
        <div className={styles.contentArea}>
          <Header />
          <SearchBar 
            searchTerm={searchTerm}
            onSearch={handleSearch}
            onNewStudent={handleNewStudent}
          />
          {loading && <div style={{ margin: '8px 0', color: '#666' }}>Cargando...</div>}
          {errorMessage && <div style={{ margin: '8px 0', color: '#b3261e' }}>{errorMessage}</div>}
          <StudentTable 
            students={filteredStudents}
            onView={handleViewStudent}
            onEdit={handleEditStudent}
            onDelete={handleDeleteStudent}
          />
        </div>
      </div>

      {showModal && (
        <NewStudentModal 
          onClose={handleCloseModal}
          onSave={handleSaveStudent}
          editMode={editMode}
          studentData={selectedStudent}
        />
      )}

      {showViewModal && selectedStudent && (
        <ViewStudentModal 
          student={selectedStudent}
          onClose={handleCloseViewModal}
        />
      )}
    </div>
  );
}

export default AdmisionEstudiantesScreen;
